import { BASE_MEASUREMENT_FIELD_DEFINITIONS } from './measurements';
import type {
  PatientMeasurementField,
  PatientMeasurementRecord,
} from '../types';

export type MeasurementChartMetricOption = {
  value: string;
  label: string;
  unit: string | null;
};

export type MeasurementChartPoint = {
  dateLabel: string;
  value: number;
  sortKey: number;
};

export type MeasurementChartSeriesDefinition = {
  name: string;
  color: string;
  label: string;
};

export type MeasurementChartDataset = {
  data: Array<Record<string, string | number>>;
  series: MeasurementChartSeriesDefinition[];
};

const CHART_SERIES_COLORS = [
  'teal.6',
  'blue.6',
  'orange.6',
  'grape.6',
  'cyan.6',
  'pink.6',
  'indigo.6',
  'lime.6',
] as const;

const EXTRA_METRIC_PREFIX = 'extra:';

export const toExtraMeasurementMetricKey = (label: string) =>
  `${EXTRA_METRIC_PREFIX}${label.trim()}`;

export const parseMeasurementNumericValue = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (normalized.length === 0) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const findMeasurementField = (
  record: PatientMeasurementRecord,
  metricKey: string
): PatientMeasurementField | undefined => {
  if (metricKey.startsWith(EXTRA_METRIC_PREFIX)) {
    const label = metricKey.slice(EXTRA_METRIC_PREFIX.length);
    return record.fields.find(
      field => field.metricType == null && field.label.trim() === label
    );
  }

  return record.fields.find(field => field.metricType === metricKey);
};

export const getMeasurementNumericValue = (
  record: PatientMeasurementRecord,
  metricKey: string
): number | null => {
  const field = findMeasurementField(record, metricKey);
  if (field == null) {
    return null;
  }

  return parseMeasurementNumericValue(field.value);
};

export const buildMeasurementChartMetricOptions = (
  history: PatientMeasurementRecord[]
): MeasurementChartMetricOption[] => {
  const options = new Map<string, MeasurementChartMetricOption>();

  BASE_MEASUREMENT_FIELD_DEFINITIONS.forEach(definition => {
    options.set(definition.metricType, {
      value: definition.metricType,
      label: definition.label,
      unit: definition.unit,
    });
  });

  history.forEach(record => {
    record.fields.forEach(field => {
      if (field.metricType != null) {
        return;
      }

      const label = field.label.trim();
      if (label.length === 0) {
        return;
      }

      const value = toExtraMeasurementMetricKey(label);
      if (!options.has(value)) {
        options.set(value, {
          value,
          label,
          unit: field.unit,
        });
      }
    });
  });

  return Array.from(options.values()).filter(option =>
    history.some(
      record => getMeasurementNumericValue(record, option.value) != null
    )
  );
};

export const buildMeasurementChartSeries = (
  history: PatientMeasurementRecord[],
  metricKey: string
): MeasurementChartPoint[] =>
  [...history]
    .reverse()
    .map(record => {
      const value = getMeasurementNumericValue(record, metricKey);
      if (value == null) {
        return null;
      }

      const createdAt = record.createdAt ?? record.updatedAt;
      const date = createdAt ? new Date(createdAt) : null;
      const sortKey =
        date != null && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
      const dateLabel =
        date != null && !Number.isNaN(date.getTime())
          ? date.toLocaleDateString('it-IT', {
              day: '2-digit',
              month: 'short',
              year: '2-digit',
            })
          : 'N/D';

      return {
        dateLabel,
        value,
        sortKey,
      };
    })
    .filter((point): point is MeasurementChartPoint => point != null)
    .sort((left, right) => left.sortKey - right.sortKey);

const formatMeasurementChartDateLabel = (
  createdAt: string | null | undefined
) => {
  const date = createdAt ? new Date(createdAt) : null;
  if (date == null || Number.isNaN(date.getTime())) {
    return { dateLabel: 'N/D', sortKey: 0 };
  }

  return {
    dateLabel: date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    }),
    sortKey: date.getTime(),
  };
};

const formatMeasurementChartSeriesLabel = (
  option: MeasurementChartMetricOption
) => (option.unit ? `${option.label} (${option.unit})` : option.label);

export const buildMeasurementChartDataset = (
  history: PatientMeasurementRecord[],
  metricKeys: string[],
  metricOptions: MeasurementChartMetricOption[]
): MeasurementChartDataset => {
  const selectedKeys = metricKeys.filter(metricKey =>
    metricOptions.some(option => option.value === metricKey)
  );

  if (selectedKeys.length === 0) {
    return { data: [], series: [] };
  }

  const points = new Map<
    number,
    Record<string, string | number> & { sortKey: number }
  >();

  [...history].reverse().forEach(record => {
    const { dateLabel, sortKey } = formatMeasurementChartDateLabel(
      record.createdAt ?? record.updatedAt
    );

    selectedKeys.forEach(metricKey => {
      const value = getMeasurementNumericValue(record, metricKey);
      if (value == null) {
        return;
      }

      const existingPoint = points.get(sortKey) ?? { dateLabel, sortKey };
      existingPoint[metricKey] = value;
      points.set(sortKey, existingPoint);
    });
  });

  const data = Array.from(points.values())
    .sort((left, right) => left.sortKey - right.sortKey)
    .map(({ sortKey: _sortKey, ...point }) => point);

  const series = selectedKeys.map((metricKey, index) => {
    const option = metricOptions.find(item => item.value === metricKey);

    return {
      name: metricKey,
      color: CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length],
      label: option ? formatMeasurementChartSeriesLabel(option) : metricKey,
    };
  });

  return { data, series };
};

export const getDefaultMeasurementChartMetric = (
  options: MeasurementChartMetricOption[]
): string | null => {
  if (options.length === 0) {
    return null;
  }

  return (
    options.find(option => option.value === 'weight_kg')?.value ??
    options[0]?.value ??
    null
  );
};

export const getDefaultMeasurementChartMetrics = (
  options: MeasurementChartMetricOption[]
): string[] => {
  const defaultMetric = getDefaultMeasurementChartMetric(options);
  return defaultMetric ? [defaultMetric] : [];
};
